import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlay } from '@fortawesome/free-solid-svg-icons';
import careManagementService from '../../services/careManagementService';
import patientService from '../../services/patientService';
import ccmService from '../../services/ccmService';
import settingsAPI from '../../services/settingsService';
import { getTodayYmdLocal, parseLocalDate } from '../../utils/dateOnly';
import { getUniqueChronicConditions } from '../../utils/patientBillingUtils';
import { extractPrincipalCondition } from '../../utils/pcmBillingUtils';
import {
  getServiceTypeDisplay,
  resolveFollowUpClockTime,
  splitFollowUpServiceTimer,
} from '../../utils/followUpDisplayUtils';
import { canEditFollowUpTime } from '../../utils/roleUtils';
import chatService from '../../services/chatService';
import { toast } from 'react-toastify';
import './AddFollowUpModal.css';
import AppDateInput from '../Common/AppDateInput/AppDateInput';
import ChatSummaryPanel from '../ChatSummaryPanel/ChatSummaryPanel';

const LEGACY_CHAT_SUMMARY_BLOCK_RE = /--- Chat summary ---[\s\S]*?--- End chat summary ---/g;

const shiftYmdLocal = (ymd, days) => {
  const base = parseLocalDate(ymd) || new Date();
  const next = new Date(base.getFullYear(), base.getMonth(), base.getDate() + days);
  const y = next.getFullYear();
  const m = String(next.getMonth() + 1).padStart(2, '0');
  const d = String(next.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const defaultChatRange = () => {
  const end = getTodayYmdLocal();
  return { start: shiftYmdLocal(end, -6), end };
};

const upsertChatSummaryBlock = (existing, summary, previousSummary = '') => {
  const next = String(summary || '').trim();
  let current = String(existing || '').replace(LEGACY_CHAT_SUMMARY_BLOCK_RE, '').trim();
  const prev = String(previousSummary || '').trim();
  if (prev && current.includes(prev)) {
    return current.replace(prev, next).replace(/\n{3,}/g, '\n\n').trim();
  }
  return current ? `${current}\n\n${next}` : next;
};

const RPM_SERVICE_TYPES = ['General', 'Call via others'];
const CCM_SERVICE_TYPES = [
  'Care plan review',
  'Medication management',
  'Patient education',
  'Care coordination',
  'Symptom / condition follow-up',
  'Other',
];
const CCM_CARE_TOPICS = [
  'Goals progress',
  'Barriers',
  'Adherence',
  'Specialist coordination',
  'Social needs',
];
const STATUS_OPTIONS = ['Continue follow up', 'No need to follow up'];

const emptyForm = (careProgram) => ({
  serviceType: ['CCM', 'PCM'].includes(String(careProgram).toUpperCase()) ? CCM_SERVICE_TYPES[0] : RPM_SERVICE_TYPES[0],
  followUpStatus: 'Continue follow up',
  assignTo: '',
  serviceTime: '',
  manualMinutes: '00',
  manualSeconds: '00',
  date: getTodayYmdLocal(),
  time: new Date().toTimeString().split(' ')[0].substring(0, 5),
  template: '',
  linkedCondition: '',
  careTopic: '',
  content: '',
});

const toYmdLocal = (raw) => {
  const date = parseLocalDate(raw);
  if (!date) return getTodayYmdLocal();
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const resolveServiceTypeForForm = (followUp, isCcm) => {
  const fallback = isCcm ? CCM_SERVICE_TYPES[0] : RPM_SERVICE_TYPES[0];
  if (!followUp?.service_type) return fallback;
  const raw = String(followUp.service_type);
  if (CCM_SERVICE_TYPES.includes(raw) || RPM_SERVICE_TYPES.includes(raw)) return raw;
  const display = getServiceTypeDisplay(followUp.service_type);
  if (CCM_SERVICE_TYPES.includes(display) || RPM_SERVICE_TYPES.includes(display)) return display;
  return fallback;
};

const buildEditForm = (followUp, isCcm) => {
  const timerParts = splitFollowUpServiceTimer(followUp);
  const mins = Number.parseInt(timerParts.minutes, 10) || 0;
  const secs = Number.parseInt(timerParts.seconds, 10) || 0;
  return {
    serviceType: resolveServiceTypeForForm(followUp, isCcm),
    followUpStatus: String(followUp.follow_up_status || '').toLowerCase().includes('no_need')
      ? 'No need to follow up'
      : 'Continue follow up',
    assignTo: followUp.assign_to || followUp.caregiver_name || followUp.author_name || '',
    serviceTime: followUp.service_time || '',
    manualMinutes: String(mins).padStart(2, '0'),
    manualSeconds: String(secs).padStart(2, '0'),
    date: toYmdLocal(followUp.date || followUp.created_at),
    time: resolveFollowUpClockTime(followUp),
    template: followUp.template_id || '',
    linkedCondition: followUp.linked_condition || '',
    careTopic: followUp.care_topic || '',
    content: followUp.fu_note_content || followUp.content || '',
  };
};

const AddFollowUpModal = ({
  isOpen,
  onClose,
  practiceId,
  patientId,
  patientName,
  patient = null,
  onSuccess,
  initialTimerValue = 0,
  careProgram = 'RPM',
  onTimerSync,
  followUpToEdit = null,
  isPhoneCall = false,
  phoneCallContext = null,
  onHangUp = null,
  chatContext = null,
}) => {
  const program = String(careProgram || 'RPM').toUpperCase();
  const isCcm = program === 'CCM' || program === 'PCM';
  const careMgmtLabel = program === 'PCM' ? 'PCM' : 'CCM';
  const isEditMode = Boolean(followUpToEdit);
  const { user } = useSelector((state) => state.auth || {});
  const canEditTime = !isEditMode || canEditFollowUpTime(user);

  const [formData, setFormData] = useState(() => emptyForm(program));
  const [timer, setTimer] = useState(initialTimerValue);
  const [isRunning, setIsRunning] = useState(false);
  const [followUpNotes, setFollowUpNotes] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contentError, setContentError] = useState('');
  const [conditionError, setConditionError] = useState('');
  const timerIntervalRef = useRef(null);
  const timerValueRef = useRef(initialTimerValue);
  const appliedCallSummaryRef = useRef('');
  const lastChatSummaryRef = useRef('');
  const hasChatContext = Boolean(chatContext?.fromUserId && chatContext?.toUserId);
  const [chatStartDate, setChatStartDate] = useState(() => defaultChatRange().start);
  const [chatEndDate, setChatEndDate] = useState(() => defaultChatRange().end);
  const [chatSummaryLoading, setChatSummaryLoading] = useState(false);
  const [chatSummaryStatus, setChatSummaryStatus] = useState('');
  const [chatSummaryError, setChatSummaryError] = useState(false);
  const [chatSummaryView, setChatSummaryView] = useState(null);

  useEffect(() => {
    timerValueRef.current = timer;
  }, [timer]);

  const serviceTypeOptions = isCcm ? CCM_SERVICE_TYPES : RPM_SERVICE_TYPES;

  const filteredTemplates = useMemo(() => {
    const list = Array.isArray(templateOptions) ? templateOptions : [];
    if (!list.length) return [];
    if (isCcm) {
      const careMgmtOnly = list.filter((t) => /ccm|pcm/i.test(String(t.name || '')));
      return careMgmtOnly.length ? careMgmtOnly : list;
    }
    const rpmOnly = list.filter((t) => !/ccm/i.test(String(t.name || '')));
    return rpmOnly.length ? rpmOnly : list;
  }, [templateOptions, isCcm]);

  useEffect(() => {
    if (isOpen) {
      if (followUpToEdit) {
        const nextForm = buildEditForm(followUpToEdit, isCcm);
        const mins = Number.parseInt(nextForm.manualMinutes || '0', 10) || 0;
        const secs = Number.parseInt(nextForm.manualSeconds || '0', 10) || 0;
        setFormData(nextForm);
        setTimer((mins * 60) + secs);
        setIsRunning(false);
      } else {
        const nextForm = emptyForm(program);
        if (isPhoneCall) {
          nextForm.serviceType = isCcm ? 'Symptom / condition follow-up' : 'Call via others';
          nextForm.content = `RingCentral call with ${patientName || 'patient'}.\n\n`;
        }
        const initialConditions = getUniqueChronicConditions(
          patient?.icd_codes ||
          patient?.chronic_conditions ||
          patient?.chronicDiseases ||
          patient?.chronic_diseases ||
          patient?.conditions ||
          patient?.diagnoses
        );
        const principal = extractPrincipalCondition(patient) || (initialConditions.length > 0 ? initialConditions[0] : '');
        if (principal && !initialConditions.includes(principal)) {
          initialConditions.unshift(principal);
        }
        if (initialConditions.length > 0) {
          setConditionOptions(initialConditions);
          nextForm.linkedCondition = principal || initialConditions[0];
        }
        setFormData(nextForm);
        setTimer(Number(initialTimerValue) || 0);
        setIsRunning(true);
        appliedCallSummaryRef.current = '';
      }
      lastChatSummaryRef.current = '';
      const range = defaultChatRange();
      setChatStartDate(range.start);
      setChatEndDate(range.end);
      setChatSummaryLoading(false);
      setChatSummaryStatus('');
      setChatSummaryError(false);
      setChatSummaryView(null);
      setContentError('');
      setConditionError('');
      fetchTemplates();
      fetchFollowUpNotes();
      if (isCcm) {
        fetchConditionOptions();
      } else {
        setConditionOptions([]);
      }
    } else {
      setIsRunning(false);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
  }, [isOpen, practiceId, patientId, careProgram, followUpToEdit, isPhoneCall, isCcm, patientName, patient]);

  useEffect(() => {
    if (!isOpen || !isPhoneCall || !phoneCallContext) return;
    const summary = String(phoneCallContext.summary || '').trim();
    if (summary && appliedCallSummaryRef.current !== summary) {
      appliedCallSummaryRef.current = summary;
      setFormData((prev) => {
        if (prev.content.includes(summary)) return prev;
        const trimmed = String(prev.content || '').trimEnd();
        return {
          ...prev,
          content: trimmed ? `${trimmed}\n\n${summary}` : summary,
        };
      });
      setContentError('');
    }
    const duration = Number(phoneCallContext.durationSeconds);
    if (Number.isFinite(duration) && duration > 0) {
      setTimer((prev) => (duration > prev ? duration : prev));
      if (['ended', 'summarizing', 'summarized', 'failed'].includes(phoneCallContext.status)) {
        setIsRunning(false);
      }
    }
  }, [isOpen, isPhoneCall, phoneCallContext]);

  const fetchTemplates = async () => {
    try {
      const response = await settingsAPI.getFollowUpTemplates();
      if (response?.success) {
        setTemplateOptions(Array.isArray(response.data) ? response.data : []);
      } else {
        setTemplateOptions([]);
      }
    } catch (error) {
      setTemplateOptions([]);
      console.error('Error fetching follow-up templates:', error);
    }
  };

  const fetchConditionOptions = async () => {
    const labels = new Set();

    const initialConditions = getUniqueChronicConditions(
      patient?.icd_codes ||
      patient?.chronic_conditions ||
      patient?.chronicDiseases ||
      patient?.chronic_diseases ||
      patient?.conditions ||
      patient?.diagnoses
    );
    initialConditions.forEach((name) => {
      if (name) labels.add(name);
    });

    const principal = extractPrincipalCondition(patient);
    if (principal) labels.add(principal);

    try {
      try {
        const enr = await ccmService.getEnrollmentByPatient(patientId, program);
        const enrollment = enr?.data?.enrollment || enr?.data || enr;
        const enrollmentId = enrollment?.enrollment_id || enrollment?.id;
        if (enrollmentId) {
          const ws = await ccmService.getCarePlanWorkspace(enrollmentId);
          const conditions = ws?.data?.conditions || ws?.conditions || [];
          conditions.forEach((c) => {
            const name = String(c.condition_name || '').trim();
            if (name) labels.add(name);
          });
        }
        if (Array.isArray(enrollment?.conditions)) {
          enrollment.conditions.forEach((c) => {
            const name = String(c.condition_name || c.name || '').trim();
            if (name) labels.add(name);
          });
        }
        if (enrollment?.chronic_conditions) {
          getUniqueChronicConditions(enrollment.chronic_conditions).forEach((name) => {
            if (name) labels.add(name);
          });
        }
        if (enrollment?.icd_codes) {
          getUniqueChronicConditions(enrollment.icd_codes).forEach((name) => {
            if (name) labels.add(name);
          });
        }
      } catch {
        // Fall through to patient fetch
      }

      if (!labels.size) {
        try {
          const details = await patientService.getPatientDetailsFast(practiceId, patientId);
          const p = details?.data?.data?.patient || details?.data?.patient || details?.data?.data || {};
          getUniqueChronicConditions(p.icd_codes || p.chronic_conditions || p.chronicDiseases || p.conditions).forEach((name) => {
            if (name) labels.add(name);
          });
          const fastPrincipal = extractPrincipalCondition(p);
          if (fastPrincipal) labels.add(fastPrincipal);
        } catch {
          // ignore
        }
      }

      if (!labels.size) {
        try {
          const resp = await patientService.getPatient(practiceId, patientId);
          const p = resp?.data?.data?.patient || resp?.data?.patient || resp?.data?.data || resp?.data || {};
          getUniqueChronicConditions(p.icd_codes || p.chronic_conditions || p.chronicDiseases || p.conditions).forEach((name) => {
            if (name) labels.add(name);
          });
          const fullPrincipal = extractPrincipalCondition(p);
          if (fullPrincipal) labels.add(fullPrincipal);
        } catch {
          // ignore
        }
      }
    } catch (error) {
      console.error(`Error loading ${careMgmtLabel} conditions for follow-up:`, error);
    }

    const optionsArray = Array.from(labels);
    if (optionsArray.length > 0) {
      setConditionOptions(optionsArray);
      setFormData((prev) => ({
        ...prev,
        linkedCondition: prev.linkedCondition || optionsArray[0],
      }));
    }
  };

  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (!isOpen || !isRunning) return undefined;
    timerIntervalRef.current = setInterval(() => {
      setTimer((prev) => prev + 1);
    }, 1000);
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isOpen, isRunning]);

  const syncTimerAndClose = () => {
    setIsRunning(false);
    if (!isEditMode && typeof onTimerSync === 'function') {
      onTimerSync(timerValueRef.current);
    }
    onClose?.();
  };

  const fetchFollowUpNotes = async () => {
    if (!practiceId || !patientId) return;
    try {
      setLoading(true);
      const response = await patientService.getFollowUps(practiceId, patientId, program);
      if (response.data && response.data.success) {
        setFollowUpNotes(response.data.data || []);
      } else {
        setFollowUpNotes([]);
      }
    } catch (error) {
      console.error('Error fetching follow-up notes:', error);
      setFollowUpNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const formatTimerDigits = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleManualTimeChange = (field, value) => {
    if (!canEditTime) return;
    const digitsOnly = String(value ?? '').replace(/\D/g, '');
    // Seconds: 0–59 (2 digits). Minutes: allow longer sessions (e.g. 109+), up to 9999.
    const normalizedValue = field === 'manualSeconds'
      ? (digitsOnly === '' ? '' : String(Math.min(59, parseInt(digitsOnly.substring(0, 2) || '0', 10))))
      : digitsOnly.substring(0, 4);

    setFormData((prev) => {
      const next = { ...prev, [field]: normalizedValue };
      const minutes = parseInt(next.manualMinutes || '0', 10) || 0;
      const secondsRaw = parseInt(next.manualSeconds || '0', 10) || 0;
      const seconds = Math.min(59, Math.max(0, secondsRaw));
      setTimer((minutes * 60) + seconds);
      return next;
    });
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'content' && String(value || '').trim()) setContentError('');
    if (field === 'linkedCondition' && String(value || '').trim()) setConditionError('');
  };

  const handleSummarizeChat = async () => {
    if (!hasChatContext || chatSummaryLoading) return;
    if (!chatStartDate || !chatEndDate) {
      setChatSummaryError(true);
      setChatSummaryStatus('Select a start and end date.');
      return;
    }
    if (chatStartDate > chatEndDate) {
      setChatSummaryError(true);
      setChatSummaryStatus('Start date cannot be after end date.');
      return;
    }
    setChatSummaryLoading(true);
    setChatSummaryError(false);
    setChatSummaryStatus('Generating summary...');
    try {
      const response = await chatService.getChatSummary(
        chatContext.fromUserId,
        chatContext.toUserId,
        {
          startDate: chatStartDate,
          endDate: chatEndDate,
          requesterUserId: chatContext.fromUserId,
        }
      );
      const payload = response?.data || {};
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to generate summary');
      }
      if (payload.empty || !String(payload.summary || payload.overallSummary || '').trim()) {
        setChatSummaryError(false);
        setChatSummaryView(null);
        setChatSummaryStatus(payload.message || 'No chat messages in this date range.');
        return;
      }
      const noteText = String(payload.overallSummary || payload.summary || '').trim();
      setChatSummaryView({
        overallSummary: noteText,
      });
      setFormData((prev) => ({
        ...prev,
        content: upsertChatSummaryBlock(prev.content, noteText, lastChatSummaryRef.current),
      }));
      lastChatSummaryRef.current = noteText;
      setContentError('');
      setChatSummaryStatus('Summary added to the note. You can edit it before saving.');
    } catch (error) {
      setChatSummaryError(true);
      setChatSummaryStatus(error.response?.data?.message || error.message || 'Failed to summarize chat');
    } finally {
      setChatSummaryLoading(false);
    }
  };

  const handleTemplateChange = (templateId) => {
    const selected = filteredTemplates.find((t) => String(t.id) === String(templateId));
    setFormData((prev) => {
      const savedSummary = String(lastChatSummaryRef.current || '').trim();
      let nextContent = String(selected?.template || '').replace(LEGACY_CHAT_SUMMARY_BLOCK_RE, '').trim();
      if (savedSummary) {
        nextContent = nextContent ? `${nextContent}\n\n${savedSummary}` : savedSummary;
      }
      return {
        ...prev,
        template: templateId,
        content: nextContent,
      };
    });
    setContentError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasError = false;
    if (!formData.content.trim()) {
      setContentError('Content is required');
      hasError = true;
    }
    if (isCcm && !String(formData.linkedCondition || '').trim()) {
      setConditionError(`Linked condition is required for ${careMgmtLabel}`);
      hasError = true;
    }
    const totalSeconds = (parseInt(formData.manualMinutes || 0, 10) || 0) * 60
      + (parseInt(formData.manualSeconds || 0, 10) || 0);
    const effectiveSeconds = totalSeconds || timer;
    const effectiveMinutes = Math.floor(Number(effectiveSeconds || 0) / 60);
    if ((!isEditMode || canEditTime) && effectiveMinutes < 1) {
      toast.error('Follow-up minutes cannot be 0');
      hasError = true;
    }
    const todayYmd = getTodayYmdLocal();
    if (!formData.date) {
      toast.error('Date is required');
      hasError = true;
    } else if (formData.date > todayYmd) {
      toast.error('Follow-up date cannot be in the future');
      hasError = true;
    }
    if (hasError) return;
    setContentError('');
    setConditionError('');

    setSubmitting(true);
    try {
      const mins = parseInt(formData.manualMinutes || 0, 10) || 0;
      const secs = Math.min(59, parseInt(formData.manualSeconds || 0, 10) || 0);
      const timerValue = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      const followUpData = {
        content: formData.content,
        service_type: formData.serviceType,
        follow_up_status: formData.followUpStatus.toLowerCase().replace(/\s+/g, '_'),
        assign_to: formData.assignTo || null,
        care_program: program,
        date: formData.date,
        time: formData.time,
        template_id: formData.template || null,
        ...(isCcm ? {
          linked_condition: formData.linkedCondition,
          care_topic: formData.careTopic || null,
        } : {}),
        ...(isEditMode
          ? (canEditTime ? {
              timerValue,
              service_time_seconds: totalSeconds || timer,
            } : {})
          : {
              service_time: formData.serviceTime || null,
              service_time_seconds: totalSeconds || timer,
            }),
      };

      const followUpId = followUpToEdit?.id || followUpToEdit?.follow_up_id;
      const response = isEditMode && followUpId
        ? await patientService.updateFollowUp(practiceId, patientId, followUpId, followUpData)
        : await careManagementService.addFollowUp(practiceId, patientId, followUpData);

      if (response.data?.success || response.status === 200) {
        toast.success(isEditMode ? 'Follow-up updated successfully' : 'Follow-up recorded successfully');
        if (!isEditMode && typeof onTimerSync === 'function') {
          onTimerSync(timerValueRef.current);
        }
        onSuccess?.({ serviceTimeSeconds: timerValueRef.current, isEdit: isEditMode });
        setIsRunning(false);
        onClose?.();
      } else {
        toast.error(response.data?.message || `Failed to ${isEditMode ? 'update' : 'save'} follow-up`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving follow-up');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateMDY = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${m}-${d}-${y}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fu-redesign-overlay" onClick={syncTimerAndClose}>
      <div className="fu-redesign-container" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="fu-redesign-close-btn-circle" onClick={syncTimerAndClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <header className="fu-redesign-header-clean">
          <div className="fu-header-titles">
            <h2 className="fu-redesign-title">
              {isEditMode ? 'Edit Follow Up' : (isPhoneCall ? 'Phone Call Follow Up' : 'Add Follow Up')} · {program}
            </h2>
          </div>
          {isPhoneCall && phoneCallContext?.statusMessage ? (
            <div className={`fu-call-status-banner is-${String(phoneCallContext.status || '').replace(/[^a-z]/g, '')}`}>
              <span className="fu-call-status-label">
                {{
                  connecting: 'Connecting',
                  calling: 'Calling',
                  connected: 'Connected',
                  ended: 'Ended',
                  failed: 'Failed',
                  summarizing: 'Ended',
                  summarized: 'Ended',
                }[phoneCallContext.status] || String(phoneCallContext.status || '')}
              </span>
              <span>{phoneCallContext.statusMessage}</span>
              {phoneCallContext.toNumber ? (
                <span className="fu-call-dest">
                  Destination: {phoneCallContext.toNumber}
                </span>
              ) : null}
              {onHangUp && ['connecting', 'calling', 'connected'].includes(phoneCallContext.status) ? (
                <button type="button" className="fu-call-hangup-btn" onClick={onHangUp}>
                  End call
                </button>
              ) : null}
            </div>
          ) : null}
        </header>

        <div className="fu-redesign-grid">
          <div className="fu-redesign-form-col">
            <div className="fu-form-bordered-box">
              <h1 className="fu-form-legend-name">{patientName || 'Patient'}</h1>
              <form onSubmit={handleSubmit}>
                <div className="fu-redesign-flex-row">
                  <div className="fu-redesign-field">
                    <label>{isCcm ? `${careMgmtLabel} Activity` : 'Service Type'}</label>
                    <select
                      value={formData.serviceType}
                      onChange={(e) => handleInputChange('serviceType', e.target.value)}
                    >
                      {serviceTypeOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div className="fu-redesign-field">
                    <label>Follow Up Status:</label>
                    <select
                      value={formData.followUpStatus}
                      onChange={(e) => handleInputChange('followUpStatus', e.target.value)}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div className="fu-redesign-field">
                    <label>Assign To</label>
                    <select
                      value={formData.assignTo}
                      onChange={(e) => handleInputChange('assignTo', e.target.value)}
                    >
                      <option value="">Select...</option>
                    </select>
                  </div>
                </div>

                <div className="fu-redesign-flex-row align-center">
                  <div className="fu-redesign-field">
                    <label>{isCcm ? `${careMgmtLabel} Care Time*` : 'Service Time*'}</label>
                    <div className={`fu-timer-chip${canEditTime ? '' : ' is-locked'}`}>
                      <span className="fu-timer-text">{formatTimerDigits(timer)}</span>
                      <button
                        type="button"
                        className="fu-timer-mini-toggle"
                        onClick={() => canEditTime && setIsRunning(!isRunning)}
                        disabled={!canEditTime}
                        title={canEditTime ? (isRunning ? 'Pause timer' : 'Start timer') : 'Only Super Admin, Practice Admin, and Provider can edit service time'}
                      >
                        {isRunning ? (
                          <div className="fu-pause-bars"><span /><span /></div>
                        ) : (
                          <FontAwesomeIcon icon={faPlay} style={{ color: canEditTime ? '#ff4d4d' : '#94a3b8' }} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="fu-redesign-field">
                    <label>Manual Min(s) *</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={formData.manualMinutes}
                      onChange={(e) => handleManualTimeChange('manualMinutes', e.target.value)}
                      placeholder="00"
                      disabled={!canEditTime}
                      title={!canEditTime ? 'Only Super Admin, Practice Admin, and Provider can edit service time' : undefined}
                    />
                  </div>

                  <div className="fu-redesign-field">
                    <label>Manual Sec(s) *</label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      value={formData.manualSeconds}
                      onChange={(e) => handleManualTimeChange('manualSeconds', e.target.value)}
                      placeholder="00"
                      disabled={!canEditTime}
                      title={!canEditTime ? 'Only Super Admin, Practice Admin, and Provider can edit service time' : undefined}
                    />
                  </div>
                </div>
                {isEditMode && !canEditTime && (
                  <p className="fu-time-lock-hint">Service time can only be changed by Super Admin, Practice Admin, or Provider.</p>
                )}

                <div className="fu-redesign-flex-row">
                  <div className="fu-redesign-field">
                    <label>Date</label>
                    <AppDateInput
                      value={formData.date}
                      max={getTodayYmdLocal()}
                      onChange={(v) => {
                        const today = getTodayYmdLocal();
                        handleInputChange('date', v && v > today ? today : v);
                      }}
                    />
                  </div>
                  <div className="fu-redesign-field">
                    <label>Time</label>
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) => handleInputChange('time', e.target.value)}
                    />
                  </div>
                  <div className="fu-redesign-field">
                    <label>Template</label>
                    <select
                      value={formData.template}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                    >
                      <option value="">Choose template</option>
                      {filteredTemplates.map((opt) => (
                        <option key={opt.id} value={opt.id}>{opt.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {isCcm && (
                  <div className="fu-redesign-flex-row">
                    <div className="fu-redesign-field">
                      <label>Linked condition *</label>
                      {program === 'PCM' ? (
                        formData.linkedCondition ? (
                          <input
                            type="text"
                            value={formData.linkedCondition}
                            readOnly
                            className={`fu-input-readonly ${conditionError ? 'fu-input-invalid' : ''}`}
                            placeholder="Principal condition"
                            title="Principal chronic condition for PCM"
                            style={{ backgroundColor: '#f8fafc', cursor: 'default' }}
                          />
                        ) : (
                          <input
                            type="text"
                            value={formData.linkedCondition}
                            onChange={(e) => handleInputChange('linkedCondition', e.target.value)}
                            placeholder="e.g. Type 2 diabetes"
                            className={conditionError ? 'fu-input-invalid' : ''}
                          />
                        )
                      ) : conditionOptions.length > 0 ? (
                        <select
                          value={formData.linkedCondition}
                          onChange={(e) => handleInputChange('linkedCondition', e.target.value)}
                          className={conditionError ? 'fu-input-invalid' : ''}
                        >
                          <option value="">Select condition</option>
                          {conditionOptions.map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={formData.linkedCondition}
                          onChange={(e) => handleInputChange('linkedCondition', e.target.value)}
                          placeholder="e.g. Type 2 diabetes"
                          className={conditionError ? 'fu-input-invalid' : ''}
                        />
                      )}
                      {conditionError && <div className="fu-field-error">{conditionError}</div>}
                      {program !== 'PCM' && !conditionOptions.length && (
                        <div className="fu-field-hint">No care-plan conditions loaded — enter the condition name manually.</div>
                      )}
                    </div>
                    <div className="fu-redesign-field">
                      <label>Care topic</label>
                      <select
                        value={formData.careTopic}
                        onChange={(e) => handleInputChange('careTopic', e.target.value)}
                      >
                        <option value="">Optional</option>
                        {CCM_CARE_TOPICS.map((topic) => (
                          <option key={topic} value={topic}>{topic}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {hasChatContext && (
                  <div className="fu-chat-summary-block">
                    <label>Chat history summary</label>
                    <div className="fu-redesign-flex-row fu-chat-summary-row">
                      <div className="fu-redesign-field">
                        <label>Start date</label>
                        <AppDateInput
                          value={chatStartDate}
                          onChange={setChatStartDate}
                          max={chatEndDate || undefined}
                        />
                      </div>
                      <div className="fu-redesign-field">
                        <label>End date</label>
                        <AppDateInput
                          value={chatEndDate}
                          onChange={setChatEndDate}
                          min={chatStartDate || undefined}
                          max={getTodayYmdLocal()}
                        />
                      </div>
                      <div className="fu-redesign-field fu-chat-summary-action">
                        <label>&nbsp;</label>
                        <button
                          type="button"
                          className="fu-btn-summarize"
                          onClick={handleSummarizeChat}
                          disabled={chatSummaryLoading}
                        >
                          {chatSummaryLoading ? 'Generating...' : 'Generate Summary'}
                        </button>
                      </div>
                    </div>
                    {chatSummaryStatus ? (
                      <div className={`fu-chat-summary-status${chatSummaryError ? ' is-error' : ''}`}>
                        {chatSummaryStatus}
                      </div>
                    ) : (
                      <div className="fu-field-hint">Generate a short clinical summary of this patient chat. Greetings are ignored.</div>
                    )}
                    {chatSummaryView ? (
                      <ChatSummaryPanel
                        overallSummary={chatSummaryView.overallSummary}
                      />
                    ) : null}
                  </div>
                )}

                <div className="fu-redesign-field fu-redesign-context-field">
                  <label>Content *</label>
                  <textarea
                    placeholder={isCcm
                      ? 'Document care management discussion, conditions addressed, and next steps...'
                      : 'Content..'}
                    value={formData.content}
                    onChange={(e) => handleInputChange('content', e.target.value)}
                    className={contentError ? 'fu-input-invalid' : ''}
                  />
                  {contentError && <div className="fu-field-error">{contentError}</div>}
                </div>

                <div className="fu-redesign-actions">
                  <button type="button" className="fu-btn-cancel" onClick={syncTimerAndClose}>Cancel</button>
                  <button type="submit" className="fu-btn-save" disabled={submitting}>
                    {submitting ? 'Saving...' : (isEditMode ? 'Update Follow Up' : 'Save')}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="fu-notes-history-col">
            <div className="fu-notes-panel">
              <h4>{program} Follow Up Notes</h4>
              <div className="fu-notes-container">
                {loading ? (
                  <p className="fu-no-notes">Loading notes...</p>
                ) : followUpNotes.length > 0 ? (
                  followUpNotes.map((note, index) => (
                    <div key={note.id || index} className="fu-note-item">
                      <div className="fu-note-header-line">
                        <span className="fu-note-author">{note.caregiver_name || 'System Caregiver'}</span>
                        <span className="fu-note-date">{formatDateMDY(note.date || note.created_at)}</span>
                      </div>
                      {note.linked_condition && (
                        <div className="fu-note-meta">Condition: {note.linked_condition}</div>
                      )}
                      {note.care_topic && (
                        <div className="fu-note-meta">Topic: {note.care_topic}</div>
                      )}
                      <p className="fu-note-text">{note.content}</p>
                    </div>
                  ))
                ) : (
                  <p className="fu-no-notes">No previous notes</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFollowUpModal;
